import BaseChart from '@components/charts/BaseChart';
import BaseCompactChart from '@components/charts/BaseCompactChart';
import config from '@config';
import type { ChartData } from 'types/common';

interface Props {
  colors: readonly string[];
  compact?: boolean;
  data: ChartData[];
  gridHorizontal?: boolean;
  gridVertical?: boolean;
  width?: number;
  height?: number;
  onSelectDataPoint?: (data: ChartData) => void;
}

function Chart(props: Props) {
  // Define the number of ticks
  const tickCount = 11;

  // Find the maximum value for setting the x-axis domain and ticks
  const maxValue = Math.max(...props.data.map(({ value }) => Math.abs(value)));

  // Calculate tick step
  const tickStep = Math.ceil(maxValue / tickCount / 10) * 10;

  // Generate ticks
  const xAxisTicks = Array.from({ length: tickCount }).map(
    (_, index) => index * tickStep
  );

  // Generate ticks for the compact chart
  const yAxisTicks = Array.from({ length: tickCount })
    .map((_, index) => index * tickStep)
    .filter((_, index) => index % 2 === 0);

  // Omit 0 and 100 from x-axis
  const formatTick = (tick: number) => {
    return tick > 0 && tick < 100 ? tick.toString() : '';
  };

  const formatLabel = (value: string) => {
    return Number(value) < 100 ? Number(value).toFixed(1) : value;
  };

  const { compact, ...restProps } = props;

  return compact ? (
    <BaseCompactChart
      {...restProps}
      height={config.chart.compact.height}
      width={config.chart.compact.width}
      gridVertical={false}
      domain={[0, maxValue]}
      yAxisTicks={yAxisTicks}
      formatLabel={formatLabel}
      formatTick={(tick) => (tick > 0 ? tick.toString() : '')}
    />
  ) : (
    <BaseChart
      {...restProps}
      domain={[0, maxValue]}
      xAxisTicks={xAxisTicks}
      formatLabel={formatLabel}
      formatTick={formatTick}
    />
  );
}

export default Chart;
